import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Calculator, Users, FileText, Clock, CheckCircle, AlertTriangle,
  TrendingUp, ArrowRight, RefreshCw, Eye
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    kalkulationen: { wartend: 0, in_bearbeitung: 0, abgeschlossen: 0 },
    akten: { total: 0, heute: 0 },
    benutzer: { total: 0, aktiv: 0 }
  });
  const [recentKalkulationen, setRecentKalkulationen] = useState([]);
  const [loading, setLoading] = useState(true);

  const getToken = () => localStorage.getItem('autogutachter24_token');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Lade Kalkulationen
      const kalkResponse = await fetch(`${API_URL}/api/kalkulation/admin/alle`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (kalkResponse.ok) {
        const kalkData = await kalkResponse.json();
        setStats(prev => ({
          ...prev,
          kalkulationen: kalkData.stats || { wartend: 0, in_bearbeitung: 0, abgeschlossen: 0 }
        }));
        setRecentKalkulationen(kalkData.kalkulationen?.slice(0, 5) || []);
      }

      // Lade Akten-Stats
      const aktenResponse = await fetch(`${API_URL}/api/akten/liste`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (aktenResponse.ok) {
        const aktenData = await aktenResponse.json();
        const akten = aktenData.akten || [];
        const heute = new Date().toISOString().split('T')[0];
        setStats(prev => ({
          ...prev,
          akten: {
            total: akten.length,
            heute: akten.filter(a => a.erstellt_am?.startsWith(heute)).length
          }
        }));
      }

      // Lade Benutzer-Stats
      const userResponse = await fetch(`${API_URL}/api/admin/users`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (userResponse.ok) {
        const userData = await userResponse.json();
        setStats(prev => ({
          ...prev,
          benutzer: {
            total: userData.users?.length || 0,
            aktiv: userData.users?.filter(u => u.aktiv !== false).length || 0
          }
        }));
      }
    } catch (error) {
      console.error('Fehler beim Laden:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      'eingereicht': { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Wartend' },
      'in_bearbeitung': { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'In Bearbeitung' },
      'abgeschlossen': { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Fertig' }
    };
    const badge = badges[status] || badges['eingereicht'];
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
            <p className="text-gray-500 mt-1">Willkommen im Backoffice-Bereich</p>
          </div>
          <Button onClick={loadDashboardData} variant="outline" className="border-gray-300 text-gray-600 hover:bg-gray-200">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Wartende Kalkulationen */}
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Wartende Kalkulationen</p>
                  <p className="text-3xl font-bold text-yellow-500 mt-1">{stats.kalkulationen.eingereicht || 0}</p>
                </div>
                <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-6 h-6 text-yellow-500" />
                </div>
              </div>
              <Button 
                onClick={() => navigate('/admin/kalkulationen')} 
                variant="ghost" 
                className="w-full mt-4 text-yellow-600 hover:bg-yellow-50"
              >
                Anzeigen <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          {/* In Bearbeitung */}
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">In Bearbeitung</p>
                  <p className="text-3xl font-bold text-blue-500 mt-1">{stats.kalkulationen.in_bearbeitung || 0}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Calculator className="w-6 h-6 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Abgeschlossen */}
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Abgeschlossen</p>
                  <p className="text-3xl font-bold text-green-500 mt-1">{stats.kalkulationen.abgeschlossen || 0}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Benutzer */}
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Benutzer</p>
                  <p className="text-3xl font-bold text-purple-500 mt-1">{stats.benutzer.total}</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-purple-500" />
                </div>
              </div>
              <Button 
                onClick={() => navigate('/admin/benutzer')} 
                variant="ghost" 
                className="w-full mt-4 text-purple-600 hover:bg-purple-50"
              >
                Verwalten <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Aktuelle Kalkulationen */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader className="border-b border-gray-200">
            <div className="flex items-center justify-between">
              <CardTitle className="text-gray-800 flex items-center">
                <Calculator className="w-5 h-5 mr-2 text-orange-500" />
                Aktuelle Kalkulationen
              </CardTitle>
              <Button 
                onClick={() => navigate('/admin/kalkulationen')} 
                variant="ghost" 
                size="sm"
                className="text-gray-500 hover:text-gray-800"
              >
                Alle anzeigen <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {recentKalkulationen.length === 0 ? (
              <div className="p-8 text-center">
                <Calculator className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Keine Kalkulationen vorhanden</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Aktenzeichen</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Fahrzeug</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Gutachter</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Eingereicht</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Aktion</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {recentKalkulationen.map((kalk) => (
                    <tr key={kalk.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <span className="font-medium text-gray-800">{kalk.aktenzeichen}</span>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-800">{kalk.fahrzeug_marke} {kalk.fahrzeug_modell}</p>
                        <p className="text-xs text-gray-500">{kalk.kennzeichen}</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{kalk.user_name}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{formatDate(kalk.eingereicht_am)}</td>
                      <td className="px-6 py-4">{getStatusBadge(kalk.status)}</td>
                      <td className="px-6 py-4 text-right">
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => navigate(`/admin/kalkulationen/${kalk.id}`)}
                          className="text-gray-500 hover:text-gray-800"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-orange-600 to-red-600 border-0 cursor-pointer hover:from-orange-500 hover:to-red-500 transition-all"
                onClick={() => navigate('/admin/kalkulationen')}>
            <CardContent className="p-6">
              <Calculator className="w-8 h-8 text-white mb-3" />
              <h3 className="text-lg font-semibold text-white">Kalkulationen bearbeiten</h3>
              <p className="text-orange-100 text-sm mt-1">Eingereichte Akten kalkulieren</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-600 to-indigo-600 border-0 cursor-pointer hover:from-blue-500 hover:to-indigo-500 transition-all"
                onClick={() => navigate('/admin/akten')}>
            <CardContent className="p-6">
              <FileText className="w-8 h-8 text-white mb-3" />
              <h3 className="text-lg font-semibold text-white">Alle Akten einsehen</h3>
              <p className="text-blue-100 text-sm mt-1">Übersicht aller Gutachten</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-600 to-pink-600 border-0 cursor-pointer hover:from-purple-500 hover:to-pink-500 transition-all"
                onClick={() => navigate('/admin/benutzer')}>
            <CardContent className="p-6">
              <Users className="w-8 h-8 text-white mb-3" />
              <h3 className="text-lg font-semibold text-white">Benutzer verwalten</h3>
              <p className="text-purple-100 text-sm mt-1">Gutachter und Admins</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
